import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  DollarSign, 
  Calendar, 
  FileText, 
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  MessageSquare
} from 'lucide-react';
import { serenityClient } from '../services/serenityClient';
import type { Case, PaymentPlan, Payment, Communication } from '../types';

interface CaseDetailPageProps {
  debtor: unknown;
}

export default function CaseDetailPage(_props: CaseDetailPageProps) {
  const { caseId } = useParams<{ caseId: string }>();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [paymentPlan, setPaymentPlan] = useState<PaymentPlan | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (caseId) {
      loadCaseData();
    }
  }, [caseId]);

  const loadCaseData = async () => {
    setIsLoading(true);
    try {
      // Load case
      const caseResult = await serenityClient.queryData<Case>(
        `SELECT * FROM Cases WHERE case_id = '${caseId}' LIMIT 1`
      );
      setCaseData(caseResult[0] || null);

      // Load payment plan
      const planResult = await serenityClient.queryData<PaymentPlan>(
        `SELECT * FROM PaymentPlans WHERE case_id = '${caseId}' ORDER BY created_at DESC LIMIT 1`
      );
      setPaymentPlan(planResult[0] || null);

      // Load payments
      const paymentsResult = await serenityClient.queryData<Payment>(
        `SELECT * FROM Payments WHERE case_id = '${caseId}' ORDER BY payment_date DESC`
      );
      setPayments(paymentsResult);

      // Load communications
      const commsResult = await serenityClient.queryData<Communication>(
        `SELECT * FROM Communications WHERE case_id = '${caseId}' ORDER BY sent_at DESC LIMIT 5`
      );
      setCommunications(commsResult);
    } catch (error) {
      console.error('Failed to load case data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
      case 'in_contact':
        return 'bg-blue-100 text-blue-800';
      case 'negotiating':
      case 'arrangement':
        return 'bg-yellow-100 text-yellow-800';
      case 'paying':
        return 'bg-green-100 text-green-800';
      case 'broken_arrangement':
      case 'pre_legal':
      case 'legal':
        return 'bg-red-100 text-red-800';
      case 'paid_in_full':
      case 'settled':
        return 'bg-emerald-100 text-emerald-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getPaymentStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Case Not Found</h2>
          <p className="text-gray-500 mb-4">The requested case could not be found.</p>
          <Link
            to="/dashboard"
            className="text-blue-600 hover:underline flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const remainingBalance = caseData.total_amount - (caseData.amount_paid || 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link
              to="/dashboard"
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Case {caseData.case_id}</h1>
                <p className="text-xs text-gray-500">Case Details</p>
              </div>
            </div>
            <span className={`ml-4 text-xs px-2 py-1 rounded-full ${getStatusColor(caseData.status)}`}>
              {getStatusLabel(caseData.status)}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Case Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Case Summary</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Original Amount</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatCurrency(caseData.original_amount ?? caseData.principal_amount, caseData.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total with Interest</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatCurrency(caseData.total_amount, caseData.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount Paid</p>
                  <p className="text-lg font-semibold text-green-600">
                    {formatCurrency(caseData.amount_paid || 0, caseData.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Remaining Balance</p>
                  <p className="text-lg font-semibold text-red-600">
                    {formatCurrency(remainingBalance, caseData.currency)}
                  </p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Days Overdue</p>
                  <p className="font-medium text-gray-900">{caseData.days_overdue} days</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Origin</p>
                  <p className="font-medium text-gray-900">{caseData.origin}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Creditor</p>
                  <p className="font-medium text-gray-900">{caseData.cedent}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Product</p>
                  <p className="font-medium text-gray-900">{caseData.product_type}</p>
                </div>
              </div>
            </div>

            {/* Payment Plan */}
            {paymentPlan && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Payment Plan</h2>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    paymentPlan.status === 'active' ? 'bg-green-100 text-green-800' :
                    paymentPlan.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {getStatusLabel(paymentPlan.status)}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Plan Type</p>
                    <p className="font-medium text-gray-900">{getStatusLabel(paymentPlan.plan_type)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Installment</p>
                    <p className="font-medium text-gray-900">
                      {formatCurrency(paymentPlan.installment_amount || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Frequency</p>
                    <p className="font-medium text-gray-900">{getStatusLabel(paymentPlan.installment_frequency || '')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Next Payment</p>
                    <p className="font-medium text-gray-900">
                      {paymentPlan.next_payment_date ? formatDate(paymentPlan.next_payment_date) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Payment History */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment History</h2>
              {payments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No payments recorded yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment) => (
                    <div
                      key={payment.payment_id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {getPaymentStatusIcon(payment.status)}
                        <div>
                          <p className="font-medium text-gray-900">
                            {formatCurrency(payment.amount, caseData.currency)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {payment.payment_date ? formatDate(payment.payment_date) : 'Pending'} · {payment.payment_method}
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                        payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {getStatusLabel(payment.status)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              <div className="space-y-3">
                <Link
                  to={`/payment/${caseData.case_id}`}
                  className="flex items-center gap-3 p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <CreditCard className="w-5 h-5" />
                  <span className="font-medium">Make a Payment</span>
                </Link>
                <Link
                  to="/chat"
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-gray-700">Discuss Payment Options</span>
                </Link>
                <Link
                  to="/documents"
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <FileText className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-gray-700">Upload Documents</span>
                </Link>
                <Link
                  to="/hardship"
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                  <span className="font-medium text-gray-700">Request Hardship</span>
                </Link>
              </div>
            </div>

            {/* Recent Communications */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Communications</h3>
              {communications.length === 0 ? (
                <p className="text-sm text-gray-500">No communications yet.</p>
              ) : (
                <div className="space-y-3">
                  {communications.map((comm) => (
                    <div key={comm.communication_id} className="border-b border-gray-100 pb-3 last:border-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          comm.channel === 'email' ? 'bg-blue-100 text-blue-700' :
                          comm.channel === 'sms' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {comm.channel.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-500">{formatDate(comm.sent_at)}</span>
                      </div>
                      <p className="text-sm text-gray-700">{comm.subject || comm.message_type}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Important Dates */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Important Dates</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Case Created</p>
                    <p className="font-medium text-gray-900">{formatDate(caseData.created_at)}</p>
                  </div>
                </div>
                {paymentPlan?.next_payment_date && (
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="text-sm text-gray-500">Next Payment Due</p>
                      <p className="font-medium text-blue-600">{formatDate(paymentPlan.next_payment_date)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
